import { Reply } from "@/types";
import AppBox from "../AppBox";
import FollowupDelayDropdown from "../AppDropdown/FollowupDelayDropdown";
import AppInput from "../AppInput";
import AppSwitch from "../AppSwitch";
import AppText from "../AppText";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { repliesUpdate } from "@/store/replies";
import AppModal from "@/components/AppModal";
import { useState } from "react";
import AppButton from "../AppButton";

interface ReplyEditorProps {
  reply: Reply;
  form: Partial<Reply>;
  onChange: (id: number, field: keyof Reply, value: any) => void;
  onToggleActive?: () => void;
  disabled?: boolean;
  isNoPhone?: boolean;
  index?: number;
  isAfterHours?: boolean;
  buttonSlot?: React.ReactNode;
}

const followupDelayList = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  ...Array.from({ length: 36 }, (_, i) => ({
    value: (i + 1) * 60,
    label: `${i + 1} ${i + 1 === 1 ? "hour" : "hours"}`,
  })),
];

const ReplyEditor: React.FC<ReplyEditorProps> = ({
    reply,
    form,
    onChange,
    onToggleActive,
    disabled,
    index = 0,
    isAfterHours = false,
    buttonSlot,
}) => {
  const dispatch = useAppDispatch();
  const { replies: allReplies } = useAppSelector((s) => s.replies);
  const isFollowup = reply.is_followup;
  const isInitial = !reply.is_followup;
  const label = isInitial && !isAfterHours
    ? "Initial Autoresponder Wording"
    : isAfterHours
      ? "After Hours Message Wording"
      : "Follow Up Wording";

  const [showPopup, setShowPopup] = useState(false);

  const handleToggleActiveInternal = async () => {
    // Allow toggling even if inputs are disabled so that a reply can be (de)activated.
    const newActive = !(form.active ?? reply.active);
    onToggleActive?.();
    await dispatch(repliesUpdate({ id: reply.id, data: { active: newActive } }));

    // Show guidance popup when enabling After Hours initial message
    if (isAfterHours && !reply.is_followup && newActive) {
      const oppositeType = reply.type === 'request' ? 'call' : 'request';
      const oppositeEnabled = allReplies.some((r) =>
        r.type === oppositeType && r.is_after_hours && !r.is_followup && r.active
      );
      if (!oppositeEnabled) {
        setShowPopup(true);
      }
    }
  };

  return (
    <AppBox gap={6} className="py-8 px-2 md:py-4 xl:py-6 xl:px-6 flex-col md:flex-row">
      <AppBox column gap={4} className="flex-1 md:flex-[0.3]">
        {isInitial ? (
          <>
            <AppSwitch
              label={isAfterHours ? "After Hours Message" : "Initial Autoresponder"}
              onChange={handleToggleActiveInternal}
              checked={form.active || false}
            />

            <AppText color={disabled ? "disabled" : "dark"} className="text-sm text-gray-500">
              {isAfterHours
                ? "If enabled, and the lead sends you a message during your After Hours (based on the hours you set), your regular messages will be disabled and we will send this special After Hours message."
                : "This message is sent instantly when a lead requests a service."}
            </AppText>


            {buttonSlot && !disabled && buttonSlot}
          </>
        ) : (
          <AppSwitch
            onChange={handleToggleActiveInternal}
            label={`${index + 1}${(index + 1) === 1 ? 'st' : (index + 1) === 2 ? 'nd' : (index + 1) === 3 ? 'rd' : 'th'} Follow Up`}
            checked={form.active || false}
          />
        )}

        {isFollowup && (
          <AppText color={disabled ? "disabled" : "dark"} className="text-sm">
            Will be sent {
              (form.delay || reply.delay) <= 30 
                ? `${form.delay || reply.delay} minutes`
                : `${(form.delay || reply.delay) / 60} ${(form.delay || reply.delay) / 60 === 1 ? 'hour' : 'hours'}`
            } after initial message
          </AppText>
        )}
      </AppBox>

      <AppBox className="flex-1 xl:flex-[0.5] gap-2 md:gap-6 flex-col md:flex-row">
        <AppBox flex={1}>
          <AppInput
            type="text"
            label={label}
            onChange={(value) => onChange(reply.id, "text", value)}
            value={form.text || ""}
            multiline
            rows={3}
            fullWidth
            disabled={disabled}
            autoResize
            resize="vertical"
          />
        </AppBox>

        {isFollowup && (
          <FollowupDelayDropdown
            followup={reply}
            label="Follow Up Delay"
            followupDelayList={followupDelayList}
            disabled={disabled}
            onChange={(newValue) => onChange(reply.id, "delay", newValue)}
          />
        )}
      </AppBox>
      {/* Guidance Modal */}
      <AppModal isOpen={showPopup} onClose={() => setShowPopup(false)} title="Heads-up!">
        <AppBox column gap={4}>
          <p className="text-sm">
            {reply.type === "request"
              ? "Do you also want to customize an After Hours message for the Missed Call auto texts? If so, please click on the ‘Missed Call Lead’ tab above."
              : "Do you also want to customize an After Hours message for the Message Lead auto texts? If so, please click on the ‘Message Lead’ tab above."}
          </p>

          <AppBox fullWidth center>
            <AppBox justify="end" width={210}>
              <AppButton onClick={() => setShowPopup(false)}>Got it</AppButton>
            </AppBox>
          </AppBox>
        </AppBox>
      </AppModal>
    </AppBox>
  );
};

export default ReplyEditor;